import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, FileText, Trash2, RefreshCw } from 'lucide-react';
import api from '../services/api';

const MyChangeRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => { fetchMyRequests(); }, []);

  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/my-change-requests');
      setRequests(response.data.data.requests || []);
    } catch (error) {
      console.error('Error:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this change request?')) return;
    try {
      setCancellingId(requestId);
      await api.delete(`/users/change-requests/${requestId}`);
      setRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to cancel request');
    } finally {
      setCancellingId(null);
    }
  };

  const statusConfig = {
    pending:  { color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', label: 'Pending Review' },
    approved: { color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', label: 'Approved' },
    rejected: { color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5', label: 'Rejected' },
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '256px', gap: '16px' }}>
        <div style={{ width: '48px', height: '48px', border: '4px solid #e59225', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading your requests...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '860px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px' }}>My Change Requests</h1>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>Track the status of your profile change requests</p>
        </div>
        <button onClick={fetchMyRequests} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {['pending', 'approved', 'rejected'].map(status => {
          const count = requests.filter(r => r.status === status).length;
          const cfg = statusConfig[status];
          return (
            <div key={status} style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ padding: '10px', borderRadius: '10px', background: cfg.bg, color: cfg.color, display: 'flex' }}>
                {status === 'pending' ? <Clock size={20} /> : status === 'approved' ? <CheckCircle size={20} /> : <XCircle size={20} />}
              </div>
              <div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827', lineHeight: 1 }}>{count}</div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px', textTransform: 'capitalize' }}>{status}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {requests.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '12px', padding: '64px 24px', textAlign: 'center', border: '1px solid #F3F4F6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ width: '72px', height: '72px', background: '#FFF7ED', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <FileText size={32} color="#e59225" />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px' }}>No Change Requests Yet</h3>
          <p style={{ color: '#6B7280', fontSize: '14px', maxWidth: '320px', margin: '0 auto' }}>You haven't submitted any profile change requests.</p>
        </div>
      ) : (
        requests.map(request => {
          const cfg = statusConfig[request.status] || statusConfig.pending;
          return (
            <div key={request.id} style={{ background: 'white', borderRadius: '12px', border: '1px solid #F3F4F6', borderLeft: `4px solid ${cfg.color}`, marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              {/* Card Header */}
              <div style={{ padding: '14px 20px', background: '#F9FAFB', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                    {request.status === 'pending' ? <Clock size={12} /> : request.status === 'approved' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {cfg.label}
                  </span>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    {new Date(request.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
                {request.status === 'pending' && (
                  <button onClick={() => handleCancelRequest(request.id)} disabled={cancellingId === request.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#FEF2F2', color: '#EF4444', border: '1px solid #FECACA', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', opacity: cancellingId === request.id ? 0.5 : 1 }}>
                    <Trash2 size={12} />
                    {cancellingId === request.id ? 'Cancelling...' : 'Cancel Request'}
                  </button>
                )}
              </div>

              {/* Card Body */}
              <div style={{ padding: '20px' }}>
                {/* Requested Changes */}
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Requested Changes</div>
                {Object.entries(request.requestedChanges || {}).map(([field, value]) => (
                  <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#F9FAFB', borderRadius: '8px', padding: '10px 14px', border: '1px solid #F3F4F6', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', width: '60px' }}>{field}</span>
                    <span style={{ color: '#e59225', fontWeight: '700', fontSize: '16px' }}>→</span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{value}</span>
                  </div>
                ))}

                {/* Reason */}
                {request.requestReason && request.requestReason !== 'Profile update request' && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Reason</div>
                    <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '10px 14px', border: '1px solid #F3F4F6', fontSize: '14px', color: '#374151' }}>{request.requestReason}</div>
                  </div>
                )}

                {/* Pending Notice */}
                {request.status === 'pending' && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: '8px', padding: '12px 14px', marginTop: '16px' }}>
                    <Clock size={14} color="#F59E0B" style={{ marginTop: '2px', flexShrink: 0 }} />
                    <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>Your request is awaiting admin review. You'll receive an email notification once it's processed.</p>
                  </div>
                )}

                {/* Review Info */}
                {request.status !== 'pending' && (
                  <div style={{ background: request.status === 'approved' ? '#ECFDF5' : '#FEF2F2', border: `1px solid ${request.status === 'approved' ? '#6EE7B7' : '#FCA5A5'}`, borderRadius: '8px', padding: '14px', marginTop: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: request.status === 'approved' ? '#065F46' : '#991B1B', margin: '0 0 6px' }}>
                      {request.status === 'approved' ? '✅ Request Approved - Your profile has been updated!' : '❌ Request Rejected'}
                    </p>
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                      Reviewed by <strong>{request.reviewer?.name}</strong> on {new Date(request.reviewedAt).toLocaleString()}
                    </p>
                    {request.reviewComments && (
                      <p style={{ fontSize: '13px', color: '#374151', margin: '8px 0 0', fontStyle: 'italic', background: 'white', padding: '8px 12px', borderRadius: '6px' }}>"{request.reviewComments}"</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default MyChangeRequestsPage;